import { useState } from "react";
import { Info, Warning, ShieldWarning, Clock, Folder, Crown, CurrencyDollar, ThumbsUp, ThumbsDown, ArrowUp, X, Alarm, EnvelopeSimple, CalendarBlank, Link as LinkIcon } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { toast } from "sonner";
import { t } from "@/lib/i18n";

const bucketColor = (b) => (b === "now" ? "bg-red-600" : b === "later" ? "bg-amber-500" : "bg-gray-400");

const hoursAgo = (iso) => {
  try { return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3600000)); } catch { return 0; }
};

const snoozePresets = () => {
  const now = new Date();
  const later = new Date(now.getTime() + 4 * 3600000);
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1); tomorrow.setHours(9, 0, 0, 0);
  const monday = new Date(now); monday.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7)); monday.setHours(9, 0, 0, 0);
  return [
    { key: "later_today", label: t("pi.sn_later", "Later today"), until: later },
    { key: "tomorrow", label: t("pi.sn_tomorrow", "Tomorrow morning"), until: tomorrow },
    { key: "monday", label: t("pi.sn_monday", "Next Monday"), until: monday },
  ];
};

const POPOVER_ROWS = (labels, weights, sig) => {
  const rows = [];
  const add = (key, name, pts) => rows.push({ key, name, pts });
  if (labels.owner) add("owner", t("pi.lbl.owner", "Addressed to you"), weights.owner);
  if (labels.money_or_deadline) add("money", t("pi.lbl.money", "Money / deadline"), weights.money);
  if (labels.their_turn) add("their_turn", t("pi.lbl.their_turn", "Awaiting your reply"), weights.their_turn);
  if (labels.ask_present) add("ask", t("pi.lbl.ask", "Asks for action"), weights.ask);
  if (labels.vip) add("vip", t("pi.lbl.vip", "VIP"), weights.vip);
  if (labels.friction) add("friction", t("pi.lbl.friction", "Frustrated tone"), weights.friction);
  if (["complaint", "escalation"].includes(labels.intent)) add("intent", `${t("pi.lbl.intent", "Intent")}: ${labels.intent}`, weights.friction);
  if (labels.nudge) add("nudge", t("pi.lbl.nudge", "Re-sent nudge"), weights.nudge);
  if (labels.urgency > 0) add("urgency", `${t("pi.lbl.urgency", "Urgency")} ${labels.urgency}/2`, labels.urgency * Math.floor(weights.urgency / 2));
  if (labels.importance > 0) add("importance", `${t("pi.lbl.importance", "Importance")} ${labels.importance}/2`, labels.importance * Math.floor(weights.importance / 2));
  if (labels.bulk) add("bulk", t("pi.lbl.bulk", "Bulk mail"), weights.bulk);
  if (labels.automation) add("automation", t("pi.lbl.automation", "Automated outreach"), weights.automation);
  if (labels.system) add("system", t("pi.lbl.system", "System sender"), weights.system);
  if (labels.linked) add("linked", t("pi.lbl.linked", "Linked to other threads"), weights.linked ?? 10);
  if (labels.meeting_soon) add("meeting", t("pi.lbl.meeting", "Meeting within 24h"), 10);
  if (sig.sender_mod) add("sender", t("pi.lbl.sender", "Sender history"), sig.sender_mod);
  (sig.taught_applied || []).forEach((ta, i) => add(`taught-${i}`, `${t("pi.lbl.taught", "Taught")}: ${ta.content?.slice(0, 40)}`, ta.delta));
  if (labels.hovering) add("hovering", t("pi.lbl.hovering", "Opened often, no action"), 5);
  const decay = (sig.effective_score ?? sig.score) - sig.score;
  if (decay < 0) add("decay", t("pi.lbl.decay", "Age decay"), decay);
  return rows;
};

export const PriorityCard = ({ sig, onOpen, onFeedback, inRest = false, dismissed = false }) => {
  const [why, setWhy] = useState(null);
  const [whyOpen, setWhyOpen] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [bundleOpen, setBundleOpen] = useState(false);
  const [pickTime, setPickTime] = useState("");
  const labels = sig.labels || {};
  const phishing = !!labels.phishing_suspect;
  const unsure = Number(labels.confidence ?? 1) < 0.7;
  const score = sig.effective_score ?? sig.score;
  const h = hoursAgo(sig.score_updated_at);

  const toggleWhy = async (e) => {
    e.stopPropagation();
    if (whyOpen) { setWhyOpen(false); return; }
    try {
      const { data } = await api.get(`/priority/signals/${sig.thread_id}`);
      setWhy(data); setWhyOpen(true);
    } catch (err) { toast.error(errDetail(err)); }
  };

  const fb = (e, verdict, until) => { e.stopPropagation(); setSnoozeOpen(false); onFeedback(sig, verdict, until); };

  return (
    <div data-testid={`priority-card-${sig.thread_id}`} onClick={() => onOpen(sig.thread_id)}
      className={`relative border rounded-md p-3 mb-2 cursor-pointer transition-colors hover:bg-gray-50 ${phishing ? "border-red-400 bg-red-50/40" : "border-gray-200 bg-white"} ${dismissed ? "opacity-60" : ""}`}>
      <div className="flex items-start gap-3">
        <span data-testid={`priority-score-${sig.thread_id}`}
          className={`shrink-0 w-9 h-9 rounded-md ${bucketColor(sig.bucket)} text-white text-sm font-bold flex items-center justify-center`}>{score}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-gray-900 truncate">{sig.from_name || sig.from_email}</span>
            <span className="text-sm text-gray-600 truncate flex-1">— {sig.subject || "(no subject)"}</span>
            <span data-testid={`priority-updated-${sig.thread_id}`} className="text-[10px] text-gray-400 shrink-0">
              {h === 0 ? t("pi.updated_now", "updated just now") : t("pi.updated_ago", "updated {h}h ago", { h })}
            </span>
          </div>
          {sig.reason && <p data-testid={`priority-reason-${sig.thread_id}`} className="text-xs text-gray-500 mt-0.5 truncate">{sig.reason}</p>}
          {sig.bundle && (
            <div data-testid={`priority-bundle-${sig.thread_id}`} className="mt-1">
              <button data-testid={`priority-bundle-toggle-${sig.thread_id}`}
                onClick={(e) => { e.stopPropagation(); setBundleOpen(!bundleOpen); }}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2.5 py-0.5 hover:bg-indigo-100">
                <LinkIcon size={11} /> {t("pi.bundle_n", "{n} linked", { n: sig.bundle.count })}
                {sig.bundle.amount ? ` · $${Number(sig.bundle.amount).toLocaleString()}` : ""}
              </button>
              {bundleOpen && (
                <div data-testid={`priority-bundle-members-${sig.thread_id}`} onClick={(e) => e.stopPropagation()}
                  className="mt-1.5 border border-indigo-100 rounded-md bg-indigo-50/40 p-2">
                  {sig.bundle.members.map((m) => (
                    <button key={m.thread_id} data-testid={`bundle-member-${m.thread_id}`}
                      onClick={() => onOpen(m.thread_id)}
                      className="w-full text-left text-xs text-gray-700 hover:text-indigo-800 py-0.5 truncate block">
                      <b>{m.score}</b> · {m.from_name} — {m.subject?.slice(0, 55)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {sig.snooze_woke && <span data-testid={`chip-snooze-woke-${sig.thread_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5"><Alarm size={11} /> {t("pi.snooze_woke", "snoozed until now")}</span>}
            {phishing && <span data-testid={`chip-phishing-${sig.thread_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-red-100 text-red-700 rounded-full px-2 py-0.5"><ShieldWarning size={11} /> {t("pi.phishing", "suspicious — never auto-acted")}</span>}
            {unsure && !phishing && <span data-testid={`chip-unsure-${sig.thread_id}`} className="text-[10px] font-semibold bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">({t("pi.unsure", "unsure")})</span>}
            {labels.vip && <span data-testid={`chip-vip-${sig.thread_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-purple-100 text-purple-700 rounded-full px-2 py-0.5"><Crown size={11} /> {t("pi.vip", "VIP")}</span>}
            {labels.their_turn && <span data-testid={`chip-turn-${sig.thread_id}`} className="text-[10px] font-semibold bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">{t("pi.awaiting_reply", "awaiting your reply")}</span>}
            {labels.money_or_deadline && <span data-testid={`chip-money-${sig.thread_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5"><CurrencyDollar size={11} /> {t("pi.money", "money/deadline")}</span>}
            {labels.friction && <span data-testid={`chip-friction-${sig.thread_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-orange-100 text-orange-700 rounded-full px-2 py-0.5"><Warning size={11} /> {t("pi.friction", "friction")}</span>}
            {labels.nudge && <span data-testid={`chip-nudge-${sig.thread_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5"><Clock size={11} /> {t("pi.nudge", "nudge")}</span>}
            {sig.draft_ready && <span data-testid={`chip-draft-${sig.thread_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5"><EnvelopeSimple size={11} /> {t("pi.draft_ready", "Draft ready")}</span>}
            {labels.meeting_soon && <span data-testid={`chip-meeting-${sig.thread_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-sky-100 text-sky-700 rounded-full px-2 py-0.5"><CalendarBlank size={11} /> {t("pi.meeting_soon", "meeting soon +10")}</span>}
            {(sig.sender_mod ?? 0) <= -10 && <span data-testid={`chip-ignore-${sig.thread_id}`} className="text-[10px] text-gray-400 italic px-1">({t("pi.usually_ignore", "you usually ignore these")})</span>}
            {sig.folder_name && <span data-testid={`chip-folder-${sig.thread_id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold bg-gray-100 text-gray-600 rounded-full px-2 py-0.5"><Folder size={11} /> {sig.folder_name}</span>}
            <span className="ml-auto inline-flex items-center gap-0.5">
              {dismissed ? (
                <button data-testid={`priority-undismiss-${sig.thread_id}`} onClick={(e) => fb(e, "undo_dismiss")}
                  className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 px-2 py-0.5">{t("pi.undismiss", "Restore")}</button>
              ) : (<>
                <button data-testid={`priority-up-${sig.thread_id}`} title={t("pi.scored_right", "Scored right")}
                  onClick={(e) => fb(e, "up")} className="p-1 text-gray-400 hover:text-emerald-600 rounded"><ThumbsUp size={13} /></button>
                <button data-testid={`priority-down-${sig.thread_id}`} title={t("pi.scored_wrong", "Scored wrong")}
                  onClick={(e) => fb(e, "down")} className="p-1 text-gray-400 hover:text-red-600 rounded"><ThumbsDown size={13} /></button>
                {inRest && (
                  <button data-testid={`priority-top-${sig.thread_id}`} title={t("pi.should_top", "Should have been up top")}
                    onClick={(e) => fb(e, "should_be_top")} className="p-1 text-gray-400 hover:text-blue-600 rounded"><ArrowUp size={13} weight="bold" /></button>
                )}
                <span className="relative">
                  <button data-testid={`priority-snooze-btn-${sig.thread_id}`} title={t("pi.snooze", "Snooze")}
                    onClick={(e) => { e.stopPropagation(); setSnoozeOpen(!snoozeOpen); }}
                    className="p-1 text-gray-400 hover:text-indigo-600 rounded"><Alarm size={13} /></button>
                  {snoozeOpen && (
                    <span data-testid={`priority-snooze-menu-${sig.thread_id}`} onClick={(e) => e.stopPropagation()}
                      className="absolute right-0 top-6 z-20 w-52 bg-white border border-gray-200 rounded-md shadow-lg p-1 block">
                      {snoozePresets().map((p) => (
                        <button key={p.key} data-testid={`priority-snooze-${p.key}-${sig.thread_id}`}
                          onClick={(e) => fb(e, "snooze", p.until.toISOString())}
                          className="w-full text-left text-xs px-2.5 py-1.5 rounded hover:bg-indigo-50 text-gray-700 block">{p.label}</button>
                      ))}
                      <span className="flex items-center gap-1 px-2 py-1.5 border-t border-gray-100 mt-1">
                        <input data-testid={`priority-snooze-pick-${sig.thread_id}`} type="datetime-local" value={pickTime}
                          onChange={(e) => setPickTime(e.target.value)} onClick={(e) => e.stopPropagation()}
                          className="flex-1 min-w-0 border border-gray-200 rounded px-1 py-0.5 text-[10px]" />
                        <button data-testid={`priority-snooze-pick-ok-${sig.thread_id}`} disabled={!pickTime}
                          onClick={(e) => fb(e, "snooze", new Date(pickTime).toISOString())}
                          className="text-[10px] font-semibold text-indigo-600 disabled:opacity-40">{t("pi.sn_ok", "OK")}</button>
                      </span>
                    </span>
                  )}
                </span>
                <button data-testid={`priority-dismiss-${sig.thread_id}`} title={t("pi.dismiss", "Dismiss")}
                  onClick={(e) => fb(e, "dismiss")} className="p-1 text-gray-400 hover:text-red-600 rounded"><X size={13} /></button>
              </>)}
              <button data-testid={`priority-why-btn-${sig.thread_id}`} onClick={toggleWhy}
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-400 hover:text-blue-600 rounded-full px-1.5 py-0.5">
                <Info size={12} /> {t("pi.why", "why?")}
              </button>
            </span>
          </div>
          {whyOpen && why && (
            <div data-testid={`priority-why-popover-${sig.thread_id}`} onClick={(e) => e.stopPropagation()}
              className="mt-2 border border-gray-200 rounded-md bg-gray-50 p-3 text-xs">
              <div className="font-semibold text-gray-700 mb-1.5">{t("pi.why_title", "Why this score")}</div>
              {POPOVER_ROWS(why.labels || {}, why.weights || {}, why).map((r) => (
                <div key={r.key} className="flex justify-between py-0.5 border-b border-gray-100 last:border-0">
                  <span className="text-gray-600">{r.name}</span>
                  <span className={`font-semibold ${r.pts >= 0 ? "text-emerald-700" : "text-red-600"}`}>{r.pts >= 0 ? `+${r.pts}` : r.pts}</span>
                </div>
              ))}
              <div className="flex justify-between pt-1.5 font-bold text-gray-800">
                <span>{t("pi.why_score", "Score")} · {t("pi.why_conf", "confidence")} {Number(why.labels?.confidence ?? 0.5).toFixed(2)}</span>
                <span>{why.effective_score}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
